import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import AuthLayout from './AuthLayout';
import AuthInput from './AuthInput';

const RequestPasswordReset: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'error' | 'success'>('success');
  const { language } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/${language}/auth/update-password`
      });

      if (error) {
        setMessageType('error');
        if (error.status === 429) {
          setMessage(t('auth.tooManyRequests'));
        } else {
          setMessage(t('auth.resetPasswordError'));
        }
        throw error;
      }
      
      setMessageType('success');
      setMessage(t('auth.resetPasswordEmailSent'));
      setEmail(''); // Clear form after success
    } catch (error) {
      console.error('Password reset error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout titleKey="auth.resetPassword">
      <h2 className="text-center text-3xl font-bold text-[var(--text-primary)]">
        {t('auth.resetPassword')}
      </h2>
      
      <form onSubmit={handleReset} className="mt-8 space-y-6">
        <AuthInput
          id="email"
          type="email"
          label={t('auth.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        {message && (
          <p className={`text-sm ${messageType === 'error' ? 'text-[var(--error)]' : 'text-[var(--success)]'} text-center`}>
            {message}
          </p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 bg-[var(--brand-primary)] text-[var(--text-button)] !important rounded-md hover:bg-[var(--brand-primary)]/90 disabled:opacity-50 transition-colors"
        >
          {isLoading ? t('auth.sending') : t('auth.sendResetLink')}
        </button>

        <button
          type="button"
          onClick={() => navigate(`/${language}/sign-in`)}
          className="w-full text-[var(--brand-primary)] hover:text-[var(--brand-primary)]/80 transition-colors"
        >
          {t('auth.backToSignIn')}
        </button>
      </form>
    </AuthLayout>
  );
};

export default RequestPasswordReset; 